import React, { memo, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useUI } from '../../context/UIContext';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { Role } from '../../types';
import Logo from './Logo';
import packageJson from '../../../package.json';

// ============================================
// Nav Item Type
// ============================================

interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
}

// ============================================
// Icons
// ============================================

const DashboardIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </svg>
);

const ProjectsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

const ProfileIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const NotificationIcon = () => (
  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

// ============================================
// Component
// ============================================

export const Sidebar: React.FC = memo(() => {
  const { currentUser } = useAuth();
  const { isMobileSidebarOpen, setMobileSidebarOpen } = useUI();
  const { isSubscribed, enablePushNotifications, error: pushError } = usePushNotifications();
  const [isEnabling, setIsEnabling] = React.useState(false);

  // Build nav items based on user role
  const navItems: NavItem[] = [
    { name: 'Dashboard', path: '/', icon: <DashboardIcon /> },
    { name: 'Projects', path: '/projects', icon: <ProjectsIcon /> },
    { name: 'My Profile', path: '/profile', icon: <ProfileIcon /> },
  ];

  if (currentUser?.role === Role.ADMIN) {
    navItems.push({ name: 'User Management', path: '/admin', icon: <UsersIcon /> });
  }

  // Close sidebar on mobile after clicking a link
  const handleLinkClick = useCallback(() => {
    if (window.innerWidth < 768) {
      setMobileSidebarOpen(false);
    }
  }, [setMobileSidebarOpen]);

  const handleClose = useCallback(() => {
    setMobileSidebarOpen(false);
  }, [setMobileSidebarOpen]);

  const handleEnableNotifications = useCallback(async () => {
    if (isEnabling) return;

    setIsEnabling(true);
    try {
      await enablePushNotifications();
    } catch (err) {
      console.error('Failed to enable push notifications:', err);
    } finally {
      setIsEnabling(false);
    }
  }, [enablePushNotifications, isEnabling]);

  return (
    <aside
      className={`
        fixed md:static inset-y-0 left-0 z-[150]
        w-64 bg-[#0f172a] text-white shadow-2xl
        transform transition-transform duration-300 ease-in-out
        flex flex-col
        ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}
      style={{ paddingTop: 'max(env(safe-area-inset-top, 0px), 20px)' }}
    >
      {/* Header */}
      <div className="p-6 pt-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo variant="white" size={40} className="drop-shadow-[0_0_8px_rgba(99,102,241,0.5)]" />
          <h1 className="text-xl font-extrabold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400 uppercase">
            WorkFlow
          </h1>
        </div>
        <button 
          className="md:hidden text-slate-400 hover:text-white transition-colors"
          onClick={handleClose}
          aria-label="Close menu"
        >
          <CloseIcon />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 space-y-1 overflow-y-auto no-scrollbar py-4">
        <p className="px-4 text-[10px] font-black uppercase tracking-widest text-slate-500 mb-4">
          Main Menu
        </p>
        
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            onClick={handleLinkClick}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all duration-300 ${
                isActive 
                  ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white shadow-lg shadow-indigo-600/30' 
                  : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-100'
              }`
            }
          >
            <span className="transition-transform group-hover:scale-110 duration-300">
              {item.icon}
            </span>
            <span className="text-sm font-semibold tracking-wide">{item.name}</span>
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-6 space-y-3">
        {/* Push Notification Prompt */}
        {!isSubscribed && (
          <div>
            <button
              onClick={handleEnableNotifications}
              disabled={isEnabling}
              className="
                w-full p-4 bg-indigo-600/20 border border-indigo-500/30 rounded-2xl
                flex flex-col items-center gap-2
                hover:bg-indigo-600/30 transition-all group
                disabled:opacity-50 disabled:cursor-not-allowed
              "
            >
              <div className={`w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center ${isEnabling ? 'animate-pulse' : 'animate-bounce'}`}>
                <NotificationIcon />
              </div>
              <div className="text-center">
                <p className="text-[10px] font-black uppercase tracking-widest text-indigo-400">
                  {isEnabling ? 'Requesting...' : 'Get Real-time Updates'}
                </p>
                <p className="text-[11px] font-bold text-white mt-0.5">
                  {isEnabling ? 'Please wait' : 'Enable Notifications'}
                </p>
              </div>
            </button>
            {pushError && (
              <p className="text-[10px] text-rose-400 mt-2 text-center px-2 leading-relaxed">
                {pushError}
              </p>
            )}
          </div>
        )}

        {/* User Profile Card */}
        <NavLink
          to="/profile"
          onClick={handleLinkClick}
          className="
            block p-4 bg-slate-800/40 rounded-2xl
            border border-slate-700/50 backdrop-blur-md
            hover:bg-slate-800/60 transition-all group
          "
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <img
                src={currentUser?.avatar}
                alt={currentUser?.name}
                className="
                  w-11 h-11 rounded-full border-2 border-indigo-500/50 shadow-md
                  group-hover:border-indigo-400 transition-colors
                "
              />
              <div className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-slate-800 rounded-full" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold truncate text-slate-100 group-hover:text-white">
                {currentUser?.name}
              </p>
              <p className="text-[9px] text-indigo-400 uppercase font-black tracking-widest leading-none mt-1">
                {currentUser?.role.replace('_', ' ')}
              </p>
            </div>
          </div>
        </NavLink>

        {/* App Version */}
        <div className="
          p-3 bg-slate-800/30 rounded-xl
          border border-slate-700/40 backdrop-blur-sm
        ">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[9px] text-slate-500 uppercase font-black tracking-widest">
                App Version
              </p>
              <p className="text-xs font-bold text-slate-300 mt-0.5">
                v{packageJson.version}
              </p>
            </div>
            <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse shadow-lg shadow-emerald-500/50"
                 title="Latest version"
            />
          </div>
        </div>
      </div>
    </aside>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;
